import { useEffect, useMemo, useRef, useState } from 'react';
import { Flex, useToast, Button, Stack, Select } from '@chakra-ui/react';
import { useCreateScript } from '../../../hooks';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../../router/routes';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useError } from '../../../error-provider/ErrorProvider';
import { GetRegistrationAgentVoices } from '../../../graphql/queries/GetRegistrationAgentVoices';
import ZaraButton from '../../../components/custom-components/buttons/Button';
import { ChangeAgentVoice } from '../../../graphql/mutations/ChangeVoiceAgent';
import { GetUserAgents } from '../../../graphql/queries/GetUserAgents';
import { GetMe } from '../../../graphql/queries/GetMe';
import CreateAgentFormWrapper from '../components/CreateAgentFormWrapper';
import { PauseIcon, PlayIcon } from 'lucide-react';

const VoicePicker = () => {
    const toast = useToast();
    const { showError } = useError();

    const [isLoading, setIsLoading] = useState(false);
    const [isPlay, setIsPlay] = useState(false);
    const [value, setValue] = useState();
    const audio = useRef(null);
    const { data: voiceData } = useQuery(GetRegistrationAgentVoices, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const voices = voiceData?.getRegistrationAgentVoices?.voices ?? [];

    const apolloClient = useApolloClient();

    const [changeVoice] = useMutation(ChangeAgentVoice, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const { data } = useQuery(GetUserAgents, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const { data: meData } = useQuery(GetMe, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const email = meData?.getMe?.email ?? '';
    const phoneNumber = meData?.getMe?.phoneNumber ?? '';
    const agentId = data?.getUserAgents?.agents[0].id;

    const currentVoice = useMemo(() => {
        if (value) {
            return (
                voiceData?.getRegistrationAgentVoices?.voices?.find((voice) => voice.text === value).previewUrl ?? ''
            );
        } else {
            return '';
        }
    }, [value, voiceData]);

    useEffect(() => {
        if (value && audio.current) {
            setIsPlay(false);
            audio.current.pause();
        }
    }, [value, audio]);

    const router = useNavigate();

    useEffect(() => {
        if (audio.current) {
            if (isPlay) {
                audio.current.play();
            } else {
                audio.current.pause();
            }
        }
    }, [isPlay, audio]);

    async function onSubmit() {
        setIsLoading(true);
        const voiceId = voices.find((voice) => voice.text === value).id;

        try {
            await changeVoice({
                variables: {
                    voiceId: Number(voiceId),
                    agentId: agentId,
                },
                onCompleted(data) {
                    if (data.changeAgentVoice.__typename === 'SuccessType') {
                        apolloClient.cache.modify({
                            id: `AgentType:${agentId}`,
                            fields: {
                                name() {
                                    return value;
                                },
                            },
                        });
                        router(PATHS.SUBSCRIPTION_PLAN);
                    } else {
                        toast({
                            title: data.changeAgentVoice.message,
                            status: 'error',
                            isClosable: true,
                        });
                        setIsLoading(false);
                    }
                },
            });
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const createScript = useCreateScript();

    useEffect(() => {
        const fbqScript = createScript(`fbq('track', 'StartTrial');`);
        const cometScript = createScript(`comet('start_trial');`);
        const gtagScript = createScript(
            `gtag('event', 'conversion', {'send_to': 'AW-11267532153/Wjt0CJHPtYwZEPnK4_wp'});`
        );

        document.body.appendChild(fbqScript);
        document.body.appendChild(cometScript);
        document.body.appendChild(gtagScript);

        return () => {
            document.body.removeChild(fbqScript);
            document.body.removeChild(cometScript);
            document.body.removeChild(gtagScript);
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (email && phoneNumber) {
            const scriptContent = `
                twq('event', 'tw-on70k-on70m', {
                    email_address: '${email}',
                    phone_number: '${phoneNumber}'
                });
            `;
            const twitterLeads = createScript(scriptContent);

            document.body.appendChild(twitterLeads);

            return () => {
                document.body.removeChild(twitterLeads);
            };
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, phoneNumber]);

    return (
        <CreateAgentFormWrapper
            title='Choose a voice for your AI'
            description={'Select a voice that best represents your brand.'}
        >
            <Stack spacing='3'>
                <audio onEnded={() => setIsPlay(false)} ref={audio} src={currentVoice} autoPlay={false} />
                <Stack>
                    <Select
                        placeholder='Select a voice'
                        onChange={(e) => {
                            setIsPlay(false);
                            setValue(e.target.value);
                        }}
                    >
                        {voices?.map((voice) => (
                            <option key={voice.id} value={voice.text}>
                                {voice.text}
                            </option>
                        ))}
                    </Select>
                    <ZaraButton
                        isDisabled={!currentVoice}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsPlay((prev) => !prev);
                        }}
                        variant='blackAlpha'
                        fontSize='sm'
                    >
                        {isPlay ? <PauseIcon className='mr-2 h-4 w-4' /> : <PlayIcon className='mr-2 h-4 w-4' />}
                        Preview Voice
                    </ZaraButton>
                </Stack>

                <Flex justifyContent='space-between'>
                    <Button onClick={() => router(PATHS.TEST_CHAT)} variant='outline' fontSize='sm'>
                        Back
                    </Button>
                    <ZaraButton
                        variant='blackAlpha'
                        isDisabled={!value || isLoading}
                        isLoading={isLoading || !agentId}
                        onClick={onSubmit}
                    >
                        Next
                    </ZaraButton>
                </Flex>
            </Stack>
        </CreateAgentFormWrapper>
    );
};

export default VoicePicker;
